import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { SingleUseTokensTable } from '@/components/user/SingleUseTokensCard';
import { userApi } from '@/lib/pillarApi';
import type {
    ListSingleUseTokensResponse,
    SingleUseTokenListItem,
} from '@pillar-portal/api-types';

export function SingleUseTokensListPage(): React.ReactElement {
    const queryClient = useQueryClient();
    const [showAllHistoricalTokens, setShowAllHistoricalTokens] = useState(false);
    const [operationMessage, setOperationMessage] = useState<string | null>(null);
    const [operationErrorMessage, setOperationErrorMessage] = useState<string | null>(null);
    const outstandingOnly: boolean = !showAllHistoricalTokens;
    const queryKeyPrefix = useMemo(
        () =>
            ['singleUseTokensPage', `outstandingOnly=${String(outstandingOnly)}`] as const,
        [outstandingOnly]
    );

    const expireMutation = useMutation({
        mutationFn: async (singleUseAuthTokenUuid: string) => {
            return await userApi.expireSingleUseTokenAdmin(singleUseAuthTokenUuid);
        },
        onSuccess: async (response) => {
            setOperationErrorMessage(null);
            setOperationMessage(response.message);
            await queryClient.invalidateQueries({ queryKey: ['singleUseTokensPage'] });
        },
        onError: (error: unknown) => {
            setOperationMessage(null);
            setOperationErrorMessage(
                error instanceof Error ? error.message : 'Failed to expire token'
            );
        },
    });

    const resendMutation = useMutation({
        mutationFn: async (singleUseAuthTokenUuid: string) => {
            return await userApi.resendSingleUseTokenAdmin(singleUseAuthTokenUuid);
        },
        onSuccess: async (response) => {
            setOperationErrorMessage(null);
            setOperationMessage(response.message);
            await queryClient.invalidateQueries({ queryKey: ['singleUseTokensPage'] });
        },
        onError: (error: unknown) => {
            setOperationMessage(null);
            setOperationErrorMessage(
                error instanceof Error ? error.message : 'Failed to resend token'
            );
        },
    });

    return (
        <InfiniteScrollList<SingleUseTokenListItem, string, typeof queryKeyPrefix>
            pageTitle="Single Use Tokens"
            cardTitle="Single Use Tokens"
            queryPlaceholder="Search by user email, token email, token type, or UUID"
            queryKeyPrefix={queryKeyPrefix}
            pageSize={100}
            fetchPage={async (params) => {
                const response: ListSingleUseTokensResponse = await userApi.listSingleUseTokensAdmin(
                    {
                        outstanding_only: outstandingOnly,
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        limit: params.limit,
                        cursor: params.cursor,
                    }
                );
                return { items: response.tokens, pagination: response.pagination };
            }}
            parseCursorParam={(pageParam: unknown) => {
                if (pageParam === null || pageParam === undefined) {
                    return undefined;
                }
                const parseResult = z.string().uuid().safeParse(pageParam);
                if (!parseResult.success) {
                    throw new Error('Expected UUID cursor');
                }
                return parseResult.data;
            }}
            controls={
                <div className="flex items-center gap-2">
                    <Checkbox
                        id="showAllSingleUseTokens"
                        checked={showAllHistoricalTokens}
                        onCheckedChange={(checked) => setShowAllHistoricalTokens(checked === true)}
                    />
                    <Label htmlFor="showAllSingleUseTokens">Show all historical tokens</Label>
                </div>
            }
            stateCopy={{
                loadingLabel: 'Loading single use tokens...',
                emptyLabel: outstandingOnly
                    ? 'No outstanding single use tokens.'
                    : 'No historical single use tokens.',
                endOfListLabel: 'End of single use tokens.',
                genericErrorLabel: 'Failed to load single use tokens.',
            }}
            renderItems={({ items }) => (
                <div className="space-y-4">
                    {operationMessage !== null ? (
                        <Alert>
                            <AlertDescription>{operationMessage}</AlertDescription>
                        </Alert>
                    ) : null}
                    {operationErrorMessage !== null ? (
                        <Alert variant="destructive">
                            <AlertDescription>{operationErrorMessage}</AlertDescription>
                        </Alert>
                    ) : null}
                    <SingleUseTokensTable
                        tokens={items}
                        showUserColumns={true}
                        isBusy={expireMutation.isPending || resendMutation.isPending}
                        onExpire={(singleUseAuthTokenId) => {
                            expireMutation.mutate(singleUseAuthTokenId);
                        }}
                        onResend={(singleUseAuthTokenId) => {
                            resendMutation.mutate(singleUseAuthTokenId);
                        }}
                    />
                </div>
            )}
        />
    );
}
